import { useState } from 'react'
import { useKnowledgeStore } from '@/stores/knowledge'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { ConfirmDeleteDialog } from '@/components/ui/confirm-delete-dialog'
import { useToast } from '@/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  CheckSquare,
  XSquare,
  Archive,
  Trash2,
  CheckCircle,
  Upload,
  Download,
  RefreshCw
} from 'lucide-react'

interface BulkOperationsProps {
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  onRefresh?: () => void
}

export function BulkOperations({ selectedIds, onSelectionChange, onRefresh }: BulkOperationsProps) {
  const { faqs, loadFAQs } = useKnowledgeStore()
  const { user } = useAuthStore()
  const { toast } = useToast()
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectAll, setSelectAll] = useState(false)
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null)

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked)
    if (checked) {
      onSelectionChange(faqs.map(faq => faq.id))
    } else {
      onSelectionChange([])
    }
  }

  const handleStatusChangeClick = (newStatus: string) => {
    if (selectedIds.length === 0) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie mindestens einen Eintrag aus',
        variant: 'destructive'
      })
      return
    }

    setPendingStatusChange(newStatus)
    setShowStatusDialog(true)
  }

  const handleBulkStatusChange = async () => {
    if (!pendingStatusChange) return

    setIsProcessing(true)

    try {
      const updateData: any = {
        status: pendingStatusChange,
        updated_at: new Date().toISOString()
      }

      // Add approval info if approving
      if (pendingStatusChange === 'approved') {
        updateData.approved_by = user?.email
        updateData.approved_at = new Date().toISOString()
        updateData.human_verified = true
      }

      const { error } = await supabase
        .from('knowledge')
        .update(updateData)
        .in('id', selectedIds)

      if (error) {
        throw error
      }

      // If archiving knowledge, also set import_sources to ignored
      if (pendingStatusChange === 'archived') {
        // Get source_uids for selected knowledge entries
        const { data: knowledgeData, error: fetchError } = await supabase
          .from('knowledge')
          .select('source_uid')
          .in('id', selectedIds)

        if (fetchError) {
          console.error('Error fetching source_uids:', fetchError)
        } else if (knowledgeData) {
          const sourceUids = knowledgeData
            .map(k => k.source_uid)
            .filter(uid => uid) // Remove null/undefined

          if (sourceUids.length > 0) {
            const { error: sourceError } = await supabase
              .from('import_sources')
              .update({
                status: 'ignored',
                updated_at: new Date().toISOString()
              })
              .in('uid', sourceUids)

            if (sourceError) {
              console.error('Error updating import_sources:', sourceError)
              toast({
                title: 'Warnung',
                description: 'Knowledge archiviert, aber Import Sources konnten nicht aktualisiert werden',
                variant: 'destructive'
              })
            }
          }
        }
      }

      toast({
        title: 'Erfolgreich',
        description: `${selectedIds.length} Einträge wurden aktualisiert`
      })

      // Clear selection and refresh
      onSelectionChange([])
      setSelectAll(false)
      await loadFAQs()
      onRefresh?.()

    } catch (error) {
      console.error('Bulk update error:', error)
      toast({
        title: 'Fehler',
        description: 'Die Massenaktualisierung ist fehlgeschlagen',
        variant: 'destructive'
      })
    } finally {
      setIsProcessing(false)
      setShowStatusDialog(false)
      setPendingStatusChange(null)
    }
  }

  const handleBulkDelete = async () => {

    setIsProcessing(true)

    try {
      const { error } = await supabase
        .from('knowledge')
        .delete()
        .in('id', selectedIds)

      if (error) {
        throw error
      }

      toast({
        title: 'Erfolgreich',
        description: `${selectedIds.length} Einträge wurden gelöscht`
      })

      // Clear selection and refresh
      onSelectionChange([])
      setSelectAll(false)
      await loadFAQs()
      onRefresh?.()

    } catch (error) {
      console.error('Bulk delete error:', error)
      toast({
        title: 'Fehler',
        description: 'Die Massenlöschung ist fehlgeschlagen',
        variant: 'destructive'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleExport = async () => {
    const itemsToExport = selectedIds.length > 0
      ? faqs.filter(faq => selectedIds.includes(faq.id))
      : faqs

    if (itemsToExport.length === 0) {
      toast({
        title: 'Fehler',
        description: 'Keine Daten zum Exportieren vorhanden',
        variant: 'destructive'
      })
      return
    }

    try {
      const dataStr = JSON.stringify(itemsToExport, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)

      const link = document.createElement('a')
      link.href = url
      link.download = `knowledge_export_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)

      toast({
        title: 'Erfolgreich',
        description: `${itemsToExport.length} Einträge wurden exportiert`
      })
    } catch (error) {
      console.error('Export error:', error)
      toast({
        title: 'Fehler',
        description: 'Der Export ist fehlgeschlagen',
        variant: 'destructive'
      })
    }
  }

  const handleRefresh = async () => {
    setIsProcessing(true)
    try {
      await loadFAQs()
      onRefresh?.()
      toast({
        title: 'Aktualisiert',
        description: 'Die FAQ-Liste wurde aktualisiert'
      })
    } catch (error) {
      console.error('Refresh error:', error)
      toast({
        title: 'Fehler',
        description: 'Die Aktualisierung ist fehlgeschlagen',
        variant: 'destructive'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          {/* Selection Controls */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectAll}
                onCheckedChange={handleSelectAll}
                disabled={isProcessing}
              />
              <span className="text-sm">
                Alle auswählen
              </span>
            </div>

            {selectedIds.length > 0 && (
              <Badge variant="secondary">
                {selectedIds.length} ausgewählt
              </Badge>
            )}
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <>
                <Select
                  disabled={isProcessing}
                  onValueChange={handleStatusChangeClick}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status ändern..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">
                      <div className="flex items-center gap-2">
                        <XSquare className="h-3 w-3" />
                        Entwurf
                      </div>
                    </SelectItem>
                    <SelectItem value="review">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="h-3 w-3" />
                        Zur Prüfung
                      </div>
                    </SelectItem>
                    <SelectItem value="approved">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3" />
                        Genehmigt
                      </div>
                    </SelectItem>
                    <SelectItem value="active">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3" />
                        Aktiv
                      </div>
                    </SelectItem>
                    <SelectItem value="archived">
                      <div className="flex items-center gap-2">
                        <Archive className="h-3 w-3" />
                        Archiviert
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                <ConfirmDeleteDialog
                  title="Bulk-Löschung bestätigen"
                  description={`Möchten Sie wirklich ${selectedIds.length} FAQ${selectedIds.length > 1 ? 's' : ''} unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
                  confirmText="Alle löschen"
                  onConfirm={handleBulkDelete}
                  trigger={
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={isProcessing}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Löschen ({selectedIds.length})
                    </Button>
                  }
                />
              </>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={isProcessing}
            >
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isProcessing}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isProcessing ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Status Summary */}
        {selectedIds.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">Ausgewählte Status:</span>
              {(() => {
                const statusCounts = selectedIds.reduce((acc, id) => {
                  const faq = faqs.find(f => f.id === id)
                  if (faq) {
                    acc[faq.status] = (acc[faq.status] || 0) + 1
                  }
                  return acc
                }, {} as Record<string, number>)

                return Object.entries(statusCounts).map(([status, count]) => (
                  <Badge key={status} variant="outline">
                    {status}: {count}
                  </Badge>
                ))
              })()}
            </div>
          </div>
        )}
      </CardContent>

      {/* Status Change Confirmation Dialog */}
      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Status ändern bestätigen</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingStatusChange && (
                <>
                  Möchten Sie den Status von {selectedIds.length} FAQ{selectedIds.length > 1 ? 's' : ''}
                  auf "{pendingStatusChange}" ändern?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Button variant="outline">Abbrechen</Button>
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkStatusChange}>
              <Button>Status ändern</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}